/**
 * Studio — interactive controls for SDF scenes.
 *
 * Adds to any scene with drag-able rect nodes:
 *   1. Tap-to-select with a dashed outline + 8 resize handles
 *   2. Drag-to-move the body of the node (selected nodes only)
 *   3. Drag a corner/side handle to resize
 *   4. Toolbar buttons: "BG" toggles fill.a, "Reset" clears persisted state
 *   5. Persistence: x/y/w/h + bg-state saved to localStorage
 *   6. Bounce dampening: large objects get scaled release velocity so they
 *      don't fly off the screen when flung.
 *
 * The hit-test extension `hitTestStudio` checks handles first, then the
 * node body, so resize takes priority over move.
 */
import type { Scene, SceneNode } from "./sceneGraph";
import { hitTest } from "./input";

export type HandleId = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

const HANDLE_SIZE = 14;   // CSS px
const HANDLE_HIT = 18;    // larger hit zone than visual size
const SELECT_OUTLINE_WIDTH = 2;
const PERSIST_PREFIX = "nspp-studio-";

export interface StudioState {
  /** Currently selected node id (null = no selection). */
  selected: number | null;
  /** When false, clicks pass through and don't select/drag/resize. */
  editing: boolean;
  /** What mode the drag is in (when pointer is down). */
  dragMode: "move" | "resize" | null;
  /** Handle being dragged, when dragMode === "resize". */
  dragHandle: HandleId | null;
  /** Snapshot at drag start — used to compute deltas for resize. */
  dragStartX: number;
  dragStartY: number;
  dragStartNodeX: number;
  dragStartNodeY: number;
  dragStartNodeW: number;
  dragStartNodeH: number;
  /** Whether a node has been hit on pointerdown (suppresses deselect). */
  hadHit: boolean;
  /** Toolbar element. */
  toolbar: HTMLDivElement | null;
  /** True while pointer is down on a handle/move — suppresses scene switch. */
  pointerActive: boolean;
}

export function createStudioState(): StudioState {
  return {
    selected: null,
    editing: false,
    dragMode: null,
    dragHandle: null,
    dragStartX: 0,
    dragStartY: 0,
    dragStartNodeX: 0,
    dragStartNodeY: 0,
    dragStartNodeW: 0,
    dragStartNodeH: 0,
    hadHit: false,
    toolbar: null,
    pointerActive: false,
  };
}

/**
 * Hit-test that also detects the 8 resize handles around the selected node.
 * Returns the handle id if a handle was hit, otherwise the node from regular
 * hit-test (or null).
 */
export function hitTestStudio(
  scene: Scene,
  state: StudioState,
  x: number,
  y: number,
): { kind: "handle"; handle: HandleId } | { kind: "node"; node: SceneNode } | { kind: "none" } {
  // 1. Handle hit-test on the selected node (highest priority).
  if (state.selected != null) {
    const sel = scene.nodes.get(state.selected);
    if (sel) {
      const handles = getHandleRects(sel);
      for (const h of handles) {
        if (
          x >= h.x - HANDLE_HIT * 0.5 &&
          x <= h.x + h.w + HANDLE_HIT * 0.5 &&
          y >= h.y - HANDLE_HIT * 0.5 &&
          y <= h.y + h.h + HANDLE_HIT * 0.5
        ) {
          return { kind: "handle", handle: h.handle };
        }
      }
    }
  }

  // 2. Regular node hit-test.
  const hit = hitTest(scene, x, y);
  if (hit) return { kind: "node", node: hit };

  return { kind: "none" };
}

interface HandleRect {
  handle: HandleId;
  x: number;
  y: number;
  w: number;
  h: number;
}

function getHandleRects(n: SceneNode): HandleRect[] {
  const s = HANDLE_SIZE;
  const cx = n.x + n.w * 0.5;
  const cy = n.y + n.h * 0.5;
  const r = n.x + n.w; // east edge
  const b = n.y + n.h; // south edge
  return [
    { handle: "nw", x: n.x - s * 0.5, y: n.y - s * 0.5, w: s, h: s },
    { handle: "n",  x: cx - s * 0.5,     y: n.y - s * 0.5, w: s, h: s },
    { handle: "ne", x: r - s * 0.5,     y: n.y - s * 0.5, w: s, h: s },
    { handle: "e",  x: r - s * 0.5,     y: cy - s * 0.5,     w: s, h: s },
    { handle: "se", x: r - s * 0.5,     y: b - s * 0.5,     w: s, h: s },
    { handle: "s",  x: cx - s * 0.5,     y: b - s * 0.5,     w: s, h: s },
    { handle: "sw", x: n.x - s * 0.5, y: b - s * 0.5,     w: s, h: s },
    { handle: "w",  x: n.x - s * 0.5, y: cy - s * 0.5,     w: s, h: s },
  ];
}

/**
 * Apply resize to the selected node based on the active handle and the
 * current pointer position. Mutates n.x/y/w/h.
 */
export function applyHandleResize(state: StudioState, ptrX: number, ptrY: number, node: SceneNode): void {
  const dx = ptrX - state.dragStartX;
  const dy = ptrY - state.dragStartY;
  const minSize = 24; // px — never shrink below a usable target
  const h = state.dragHandle;
  if (!h) return;

  let nx = state.dragStartNodeX;
  let ny = state.dragStartNodeY;
  let nw = state.dragStartNodeW;
  let nh = state.dragStartNodeH;

  if (h === "nw" || h === "w" || h === "sw") {
    nx = state.dragStartNodeX + dx;
    nw = state.dragStartNodeW - dx;
    if (nw < minSize) { nx -= (minSize - nw); nw = minSize; }
  }
  if (h === "ne" || h === "e" || h === "se") {
    nw = state.dragStartNodeW + dx;
    if (nw < minSize) nw = minSize;
  }
  if (h === "nw" || h === "n" || h === "ne") {
    ny = state.dragStartNodeY + dy;
    nh = state.dragStartNodeH - dy;
    if (nh < minSize) { ny -= (minSize - nh); nh = minSize; }
  }
  if (h === "sw" || h === "s" || h === "se") {
    nh = state.dragStartNodeH + dy;
    if (nh < minSize) nh = minSize;
  }

  node.x = nx;
  node.y = ny;
  node.w = nw;
  node.h = nh;
}

/**
 * Dampen a release velocity for big objects so they don't fly off-screen.
 *   mass = sqrt(area) in CSS px (rough proxy).
 *   factor = clamp(40 / mass, 0.15, 1.0)
 * A 100px² object → factor 0.4. A 30px² object → factor 1.0. A 300px²
 * object → factor 0.15 (essentially no throw).
 */
export function dampenForMass(vx: number, vy: number, n: SceneNode): { vx: number; vy: number } {
  const mass = Math.sqrt(Math.max(1, n.w * n.h));
  const factor = Math.max(0.15, Math.min(1.0, 40 / mass));
  return { vx: vx * factor, vy: vy * factor };
}

/**
 * Persist a node's position/size/visible-bg to localStorage under the
 * scene-specific key. Stores an array of `{id, x, y, w, h, hasBg}`.
 */
export function persistScene(sceneName: string, scene: Scene): void {
  if (typeof localStorage === "undefined") return;
  const data: Array<{ id: number; x: number; y: number; w: number; h: number; hasBg: boolean }> = [];
  for (const [id, n] of scene.nodes) {
    data.push({
      id,
      x: n.x,
      y: n.y,
      w: n.w,
      h: n.h,
      hasBg: n.fill.a > 0.001,
    });
  }
  try {
    localStorage.setItem(PERSIST_PREFIX + sceneName, JSON.stringify(data));
  } catch {
    // ignore quota / disabled storage
  }
}

export function loadSceneState(sceneName: string): Array<{ id: number; x: number; y: number; w: number; h: number; hasBg: boolean }> | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + sceneName);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function applyPersistedState(
  scene: Scene,
  data: Array<{ id: number; x: number; y: number; w: number; h: number; hasBg: boolean }>,
): void {
  for (const d of data) {
    const n = scene.nodes.get(d.id);
    if (!n) continue;
    n.x = d.x;
    n.y = d.y;
    n.w = d.w;
    n.h = d.h;
    // Toggle fill alpha to match persisted bg state.
    const origAlpha = n.fill.a;
    n.fill = { ...n.fill, a: d.hasBg ? Math.max(0.85, origAlpha) : 0 };
  }
}

export function clearPersistedState(sceneName: string): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.removeItem(PERSIST_PREFIX + sceneName); } catch { /* noop */ }
}

export const STUDIO_CONSTANTS = {
  HANDLE_SIZE,
  SELECT_OUTLINE_WIDTH,
  PERSIST_PREFIX,
};